"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePrefs } from "@/components/providers/app-providers";

type Mode = "login" | "register";

export function AuthForm({ mode }: { mode: Mode }) {
  const { t } = usePrefs();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
      };
      if (!res.ok) {
        if (data.code === "EMAIL_TAKEN") setError(t("errEmailTaken"));
        else if (data.code === "INVALID_CREDENTIALS") setError(t("errInvalidCreds"));
        else if (data.code === "RATE_LIMIT") setError(t("errRateLimit"));
        else setError(data.error || t("errGeneric"));
        setLoading(false);
        return;
      }
      // Hard navigation immediately — don't wait for React effects / soft router.
      window.location.href = "/dashboard";
    } catch {
      setError(t("networkError"));
      setLoading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto w-full max-w-md space-y-5" noValidate>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {mode === "login" ? t("loginTitle") : t("registerTitle")}
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {mode === "login" ? t("loginSubtitle") : t("registerSubtitle")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">{t("email")}</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          dir="ltr"
          className="text-left"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">{t("password")}</Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          dir="ltr"
          className="text-left"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
        {mode === "register" && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{t("passwordHint")}</p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-200">
          {error}
        </div>
      )}

      <Button type="submit" className="w-full" disabled={loading}>
        {loading
          ? t("redirecting")
          : mode === "login"
            ? t("login")
            : t("createAccount")}
      </Button>

      <p className="text-center text-sm text-slate-600 dark:text-slate-300">
        {mode === "login" ? (
          <>
            {t("noAccount")}{" "}
            <Link href="/register" className="font-medium text-teal-700 hover:underline dark:text-teal-400">
              {t("register")}
            </Link>
          </>
        ) : (
          <>
            {t("alreadyHaveAccount")}{" "}
            <Link href="/login" className="font-medium text-teal-700 hover:underline dark:text-teal-400">
              {t("login")}
            </Link>
          </>
        )}
      </p>
    </form>
  );
}
